import { Component, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { QUESTIONS } from "./data/quiz";
import { Intro } from "./components/Intro";
import { RealityCheck } from "./components/RealityCheck";
import { Question } from "./components/Question";
import { Result } from "./components/Result";
import { Footer } from "./components/Footer";
import {
  ConfirmatPage,
  DezabonatPage,
  ConfidentialitatePage,
  TermeniPage,
  ContactPage,
} from "./components/SimplePages";
import { AdminPage } from "./components/AdminPage";

const STORAGE_KEY = "mr_quiz_state_v2";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-base px-6 text-center text-ink">
          <p>Ceva n-a mers bine. Reîncarcă pagina.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!Array.isArray(s.answers) || s.answers.length !== QUESTIONS.length) return null;
    return s;
  } catch {
    return null;
  }
}

function QuizApp() {
  const [initial] = useState(
    () =>
      loadState() || {
        screen: "intro",
        current: 0,
        answers: Array(QUESTIONS.length).fill(null),
        screenTime: null,
      },
  );
  const [screen, setScreen] = useState(initial.screen);
  const [current, setCurrent] = useState(initial.current);
  const [answers, setAnswers] = useState(initial.answers);
  const [screenTime, setScreenTime] = useState(initial.screenTime);
  const timerRef = useRef(null);
  const lenisRef = useRef(null);
  const dirRef = useRef(1);
  const lockedRef = useRef(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.12 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ screen, current, answers, screenTime }));
    } catch {
      /* private mode — progress rămâne doar în memorie */
    }
  }, [screen, current, answers, screenTime]);

  const scrollTop = useCallback(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  const start = () => {
    dirRef.current = 1;
    setScreen("check");
    scrollTop();
  };

  const submitScreenTime = (range) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setScreenTime(range);
    dirRef.current = 1;
    timerRef.current = setTimeout(() => {
      setScreen("quiz");
      setCurrent(0);
      scrollTop();
      lockedRef.current = false;
    }, 350);
  };

  const answer = (option) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setAnswers((prev) => prev.map((a, i) => (i === current ? option : a)));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dirRef.current = 1;
      if (current < QUESTIONS.length - 1) setCurrent(current + 1);
      else setScreen("result");
      scrollTop();
      lockedRef.current = false;
    }, 380);
  };

  const back = () => {
    clearTimeout(timerRef.current);
    dirRef.current = -1;
    if (screen === "check") setScreen("intro");
    else if (current === 0) setScreen("check");
    else setCurrent(current - 1);
    scrollTop();
  };

  const status =
    screen === "intro"
      ? "Pagina de start."
      : screen === "check"
        ? "Reality check: alege intervalul de timp."
        : screen === "quiz"
          ? `Întrebarea ${current + 1} din ${QUESTIONS.length}.`
          : "Rezultatul tău este gata.";

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#continut"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-teal focus:px-5 focus:py-3 focus:text-base"
      >
        Sari la conținut
      </a>
      <p role="status" aria-live="polite" data-testid="live-status" className="sr-only">
        {status}
      </p>
      <div id="continut" className="mx-auto flex w-full max-w-[520px] flex-1 flex-col px-6">
        <header className="pt-8">
          <div className="flex items-center" data-testid="brand">
            <a href="/" aria-label="MindRegen — pagina principală" className="inline-block">
              <img
                src="/logo.png"
                alt="MindRegen"
                data-testid="brand-logo"
                className="h-14 w-14 rounded-full ring-1 ring-white/10 md:h-[72px] md:w-[72px]"
              />
            </a>
          </div>
        </header>
        <div className="flex-1">
          {screen === "intro" && <Intro onStart={start} />}
          {screen === "check" && (
            <RealityCheck selected={screenTime} onSubmit={submitScreenTime} onBack={back} />
          )}
          {screen === "quiz" && (
            <Question
              index={current}
              total={QUESTIONS.length}
              question={QUESTIONS[current]}
              selected={answers[current]}
              dir={dirRef.current}
              onAnswer={answer}
              onBack={back}
            />
          )}
          {screen === "result" && <Result answers={answers} screenTime={screenTime} />}
        </div>
        <Footer variant={screen === "intro" || screen === "result" ? "full" : "minimal"} />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<QuizApp />} />
          <Route path="/confirmat" element={<ConfirmatPage />} />
          <Route path="/dezabonat" element={<DezabonatPage />} />
          <Route path="/confidentialitate" element={<ConfidentialitatePage />} />
          <Route path="/termeni" element={<TermeniPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
